import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PageBanner, Spinner, toast } from "@performyard/py-components";

import {
	VIDEO_CONFERENCING_PROVIDER_CONFIG,
	isVideoConferencingProvider,
} from "../../../../components/VideoConferencing";
import { CalendarSection } from "./components/CalendarSection";
import { MeetingsIntegrationsHeader } from "./components/MeetingsIntegrationsHeader";
import { VideoConferencingSection } from "./components/VideoConferencingSection";

import ErrorPage from "../../../../pages/Error/ErrorPage";

import { useMeetingsIntegrationsSettings } from "../../../../api/hooks/defs/meetings-integrations";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

import { CALENDAR_PROVIDER_DISPLAY_NAMES, CalendarProvider } from "./config";

// Status queries aren't pre-fetched: their endpoints 403 when a section's FF/admin toggle is off.
export const meetingsIntegrationsViewLoader = () => {
	return dataLoader([[currentUserQuery]]);
};

const isCalendarProvider = (value: string): value is CalendarProvider => value === "google" || value === "outlook";

const resolveDisplayName = (providerParam: string | null): string => {
	if (!providerParam) {
		return "integration";
	}
	if (isCalendarProvider(providerParam)) {
		return CALENDAR_PROVIDER_DISPLAY_NAMES[providerParam];
	}
	if (isVideoConferencingProvider(providerParam)) {
		return VIDEO_CONFERENCING_PROVIDER_CONFIG[providerParam].longName;
	}
	return providerParam;
};

export const MeetingsIntegrationsView = (): React.ReactElement => {
	const [searchParams, setSearchParams] = useSearchParams();
	const queryClient = useQueryClient();
	// Tracks the last handled success/provider combo to keep the effect idempotent.
	const handledOAuthResultRef = useRef<string | null>(null);
	// Held in state, not read from the URL: the effect below strips the OAuth params once handled.
	const [connectBanner, setConnectBanner] = useState<{ mainTitle: string; subTitle: string } | null>(null);

	const { value: meetingsIntegrationsFFOn, query: meetingsIntegrationsFFQuery } = useFeatureFlagQuery(
		"ff-is-meetings-integrations-enabled",
		false
	);
	const { data: meetingsIntegrationsSettings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({
		enabled: meetingsIntegrationsFFOn,
	});

	const isCalendarSectionVisible = meetingsIntegrationsFFOn && !!meetingsIntegrationsSettings?.calendar?.enabled;
	const isVideoConferencingSectionVisible =
		meetingsIntegrationsFFOn && !!meetingsIntegrationsSettings?.video_conferencing?.enabled;

	// Unified OAuth success/failure toast for calendar + video conferencing callbacks.
	useEffect(() => {
		const successParam = searchParams.get("success");
		const providerParam = searchParams.get("provider");
		const reasonParam = searchParams.get("reason");

		if (!successParam && !providerParam) {
			return;
		}

		const resultKey = `${successParam ?? ""}|${providerParam ?? ""}|${reasonParam ?? ""}`;
		if (handledOAuthResultRef.current === resultKey) {
			return;
		}
		handledOAuthResultRef.current = resultKey;

		if (successParam) {
			const displayName = resolveDisplayName(providerParam);
			if (successParam === "true") {
				toast.success(`${displayName} successfully connected!`);
				if (providerParam && isCalendarProvider(providerParam)) {
					queryClient.invalidateQueries({ queryKey: ["user-calendar-integration-status"] });
				} else if (providerParam && isVideoConferencingProvider(providerParam)) {
					queryClient.invalidateQueries({ queryKey: ["user-video-conferencing-status"] });
				}
			} else if (successParam === "false") {
				// Provider-specific connect-failure copy (e.g. Teams' admin-consent hint) is centralized
				// in config; fall back to a generic retry message when a provider defines none.
				const providerConfig =
					providerParam && isVideoConferencingProvider(providerParam)
						? VIDEO_CONFERENCING_PROVIDER_CONFIG[providerParam]
						: undefined;
				// A refusal the user can't retry their way out of persists as a banner; everything else
				// is a transient failure and stays a toast.
				const personalAccountBanner =
					reasonParam === "personal-account" ? providerConfig?.connectPersonalAccountBanner : undefined;
				if (personalAccountBanner) {
					setConnectBanner(personalAccountBanner);
				} else {
					const providerFailureMessage =
						(reasonParam === "unlicensed" ? providerConfig?.connectUnlicensedMessage : undefined) ??
						providerConfig?.connectFailureMessage;
					toast.error(providerFailureMessage ?? `Failed to connect ${displayName}. Please try again.`);
				}
			}
		}

		// Construct a fresh URLSearchParams — mutating the tracked object causes re-render bugs.
		const next = new URLSearchParams(searchParams);
		next.delete("success");
		next.delete("provider");
		next.delete("reason");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams, queryClient]);

	const isLoadingGating = meetingsIntegrationsFFQuery.isLoading || isSettingsLoading;

	if (isLoadingGating) {
		return (
			<div className="flex h-[208px] w-full">
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			</div>
		);
	}

	if (!isCalendarSectionVisible && !isVideoConferencingSectionVisible) {
		return <ErrorPage code={403} />;
	}

	return (
		<div className="flex w-full flex-col gap-6">
			{connectBanner && (
				<PageBanner
					color="red"
					size="small"
					mainTitle={connectBanner.mainTitle}
					subTitle={connectBanner.subTitle}
					onDismiss={() => setConnectBanner(null)}
					data-testid="connect-failure-banner"
				/>
			)}
			<MeetingsIntegrationsHeader />
			{isCalendarSectionVisible && <CalendarSection />}
			{isVideoConferencingSectionVisible && <VideoConferencingSection />}
		</div>
	);
};
